import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, Save } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { withOwner } from "@/lib/supabase/withOwner";
import { toast } from "@/lib/safeToast";
import { updatePatientSubstanceResponseProfile } from "@/lib/patientLearning/updateResponseProfile";
import { mirrorCheckinEvent } from "@/lib/clinical/eventPersistence";

const CORE_AXES: { key: string; label: string }[] = [
  { key: "mood_0_10", label: "humor" },
  { key: "energy_0_10", label: "energia" },
  { key: "anxiety_0_10", label: "ansiedade" },
  { key: "focus_0_10", label: "foco" },
  { key: "motivation_0_10", label: "motivação" },
  { key: "anhedonia_0_10", label: "anedonia" },
  { key: "sleep_quality_0_10", label: "qualidade do sono" },
  { key: "insomnia_0_10", label: "insônia" },
  { key: "sedation_0_10", label: "sedação" },
];

const ADVANCED_AXES: { key: string; label: string }[] = [
  { key: "irritability_0_10", label: "irritabilidade" },
  { key: "impulsivity_0_10", label: "impulsividade" },
  { key: "rumination_0_10", label: "ruminação" },
  { key: "craving_0_10", label: "craving/fissura" },
  { key: "withdrawal_0_10", label: "abstinência" },
  { key: "paranoia_0_10", label: "paranoia/desconfiança" },
  { key: "ideas_of_reference_0_10", label: "ideias de referência" },
  { key: "cognitive_overload_0_10", label: "sobrecarga cognitiva" },
  { key: "appetite_0_10", label: "apetite" },
  { key: "cardiovascular_0_10", label: "sintomas cardiovasculares" },
];

function ScaleField({ label, value, onChange }: { label: string; value: number | null; onChange: (value: number) => void }) {
  return (
    <div className="min-w-0 rounded-xl border border-border/60 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="min-w-0 break-words text-xs font-medium">{label}</span>
        <span className="shrink-0 font-mono text-xs tabular-nums">{value == null ? "—" : `${value}/10`}</span>
      </div>
      <Slider
        aria-label={label}
        value={[value ?? 0]}
        min={0}
        max={10}
        step={1}
        onValueChange={([next]) => onChange(next)}
        className="w-full min-w-0"
      />
    </div>
  );
}

export function SessionCheckin({ patientId, sessionId, onSaved }: { patientId: string; sessionId?: string | null; onSaved?: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, number>>({});
  const [targetValues, setTargetValues] = useState<Record<string, number>>({});
  const [text, setText] = useState({ patient_report: "", physician_observation: "", adverse_event: "", trigger_text: "", context_text: "" });
  const [saving, setSaving] = useState(false);

  const { data: targetSymptoms = [], isLoading: loadingTargets } = useQuery({
    queryKey: ["checkin-target-symptoms", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("patient_target_symptoms")
        .select("*")
        .eq("patient_id", patientId)
        .eq("status", "ativo")
        .is("deleted_at", null)
        .order("priority", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  useEffect(() => {
    setValues({});
    setTargetValues({});
    setText({ patient_report: "", physician_observation: "", adverse_event: "", trigger_text: "", context_text: "" });
  }, [patientId]);

  async function save() {
    if (!user || !patientId) return;
    const hasScale = Object.keys(values).length > 0;
    const hasTarget = Object.keys(targetValues).length > 0;
    const hasText = Object.values(text).some((value) => value.trim());
    if (!hasScale && !hasTarget && !hasText) {
      toast.error("Preencha ao menos uma escala, sintoma-alvo ou observação.");
      return;
    }

    setSaving(true);
    const measuredAt = new Date().toISOString();
    try {
      const payload = withOwner({
        patient_id: patientId,
        session_id: sessionId ?? null,
        checkin_at: measuredAt,
        ...values,
        ...text,
      }, user);
      const { data: savedCheckin, error } = await (supabase as any)
        .from("session_checkins")
        .insert(payload)
        .select("*")
        .single();
      if (error) throw error;

      try {
        await mirrorCheckinEvent(user, savedCheckin);
      } catch (mirrorError) {
        console.warn("[SessionCheckin] timeline mirror failed", mirrorError);
      }

      const measurementRows = (targetSymptoms as any[])
        .filter((target) => targetValues[target.id] != null)
        .map((target) => withOwner({
          patient_id: patientId,
          target_symptom_id: target.id,
          session_id: sessionId ?? null,
          symptom_name: target.symptom_name,
          measured_at: measuredAt,
          intensity_0_10: targetValues[target.id],
          context: text.context_text || text.trigger_text || null,
          notes: text.patient_report || text.adverse_event || null,
        }, user));

      if (measurementRows.length) {
        const { error: measurementError } = await (supabase as any)
          .from("symptom_measurements")
          .insert(measurementRows);
        if (measurementError) throw measurementError;
      }

      try {
        const since = new Date(Date.now() - 24 * 3600_000).toISOString();
        const { data: recent } = await (supabase as any)
          .from("medication_dose_logs")
          .select("*")
          .eq("patient_id", patientId)
          .gte("actual_time", since)
          .is("deleted_at", null)
          .order("actual_time", { ascending: false });
        if (recent?.length) {
          await updatePatientSubstanceResponseProfile({
            user,
            patientId,
            sessionId: sessionId ?? null,
            checkin: { ...values },
            recentDoses: recent as any,
          });
        }
      } catch (learningError) {
        console.warn("[SessionCheckin] individual learning skipped", learningError);
      }

      toast.success(measurementRows.length
        ? `Check-in e ${measurementRows.length} medida(s) de sintomas-alvo salvos.`
        : "Check-in salvo. Previsibilidade individual recalculada quando havia dose recente.");
      setValues({});
      setTargetValues({});
      setText({ patient_report: "", physician_observation: "", adverse_event: "", trigger_text: "", context_text: "" });

      await Promise.all([
        qc.invalidateQueries({ queryKey: ["checkins"] }),
        qc.invalidateQueries({ queryKey: ["journal-checkins", patientId] }),
        qc.invalidateQueries({ queryKey: ["psylog-checkins", patientId] }),
        qc.invalidateQueries({ queryKey: ["psylog-events", patientId] }),
        qc.invalidateQueries({ queryKey: ["my-space-checkins", patientId] }),
        qc.invalidateQueries({ queryKey: ["ci-checkins", patientId] }),
        qc.invalidateQueries({ queryKey: ["ci-symptom-measurements", patientId] }),
        qc.invalidateQueries({ queryKey: ["target-symptom-timeline", patientId] }),
        qc.invalidateQueries({ queryKey: ["resp-profiles"] }),
        qc.invalidateQueries({ queryKey: ["recent-doses", patientId] }),
      ]);
      console.info("[SessionCheckin] saved", { id: savedCheckin?.id, patientId, targetMeasurements: measurementRows.length });
      onSaved?.();
    } catch (saveError: any) {
      console.error("[SessionCheckin] save failed", saveError);
      toast.error(saveError?.message ?? "Falha ao salvar check-in.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="min-w-0 space-y-4 overflow-hidden p-4">
      <div>
        <div className="flex items-center gap-2 text-sm font-medium">
          <Activity className="h-4 w-4 text-primary" /> Check-in clínico real
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          Escalas 0–10. A sessão é opcional: o check-in é salvo no prontuário longitudinal mesmo sem sessão e aparece no Journal/Timeline. Associação temporal não prova causalidade.
        </p>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
        {CORE_AXES.map((axis) => (
          <ScaleField
            key={axis.key}
            label={axis.label}
            value={values[axis.key] ?? null}
            onChange={(next) => setValues((current) => ({ ...current, [axis.key]: next }))}
          />
        ))}
      </div>

      <details className="rounded-xl border p-3">
        <summary className="cursor-pointer text-sm font-medium">Escalas avançadas</summary>
        <div className="mt-3 grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
          {ADVANCED_AXES.map((axis) => (
            <ScaleField
              key={axis.key}
              label={axis.label}
              value={values[axis.key] ?? null}
              onChange={(next) => setValues((current) => ({ ...current, [axis.key]: next }))}
            />
          ))}
        </div>
      </details>

      <div className="space-y-2 rounded-xl border border-primary/20 bg-primary/5 p-3">
        <div>
          <div className="text-sm font-medium">Sintomas-alvo do tratamento</div>
          <p className="text-xs text-muted-foreground">Cada valor gera uma medida longitudinal vinculada ao alvo clínico.</p>
        </div>
        {loadingTargets ? (
          <div className="text-sm text-muted-foreground">Carregando alvos…</div>
        ) : targetSymptoms.length ? (
          <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
            {(targetSymptoms as any[]).map((target) => (
              <ScaleField
                key={target.id}
                label={`${target.symptom_name}${target.therapeutic_goal != null ? ` · meta ${target.therapeutic_goal}/10` : ""}`}
                value={targetValues[target.id] ?? null}
                onChange={(next) => setTargetValues((current) => ({ ...current, [target.id]: next }))}
              />
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">Nenhum sintoma-alvo ativo. Eles podem ser cadastrados na área clínica.</div>
        )}
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="min-w-0 space-y-1">
          <Label className="text-[11px] uppercase text-muted-foreground">Relato do paciente</Label>
          <Textarea rows={3} value={text.patient_report} onChange={(event) => setText({ ...text, patient_report: event.target.value })} placeholder="Queixa, benefício percebido, dificuldade ou mudança desde o último registro…" />
        </div>
        <div className="min-w-0 space-y-1">
          <Label className="text-[11px] uppercase text-muted-foreground">Observação médica</Label>
          <Textarea rows={3} value={text.physician_observation} onChange={(event) => setText({ ...text, physician_observation: event.target.value })} placeholder="Hipóteses, exame do estado mental ou ressalvas clínicas…" />
        </div>
        <div className="min-w-0 space-y-1">
          <Label className="text-[11px] uppercase text-muted-foreground">Efeito adverso</Label>
          <Textarea rows={3} value={text.adverse_event} onChange={(event) => setText({ ...text, adverse_event: event.target.value })} placeholder="Descreva intensidade, início e evolução…" />
        </div>
        <div className="min-w-0 space-y-1">
          <Label className="text-[11px] uppercase text-muted-foreground">Gatilho e contexto</Label>
          <Textarea rows={3} value={text.context_text} onChange={(event) => setText({ ...text, context_text: event.target.value })} placeholder="Sono, alimentação, cafeína, estresse, ambiente, horário…" />
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="w-full sm:w-auto">
          <Save className="mr-1.5 h-4 w-4" />{saving ? "Salvando…" : "Salvar check-in"}
        </Button>
      </div>
    </Card>
  );
}
